import React from 'react'

import * as c from '../../../../common'
import {
  TOTAL_PENDING_LABEL,
  totalCostPendingApply,
} from '../../../../common/helpers/totalCostDisplay'
import {
  MONITORING_MONTHLY,
  appliedMonthlyPayment,
  effectiveMonthlyPayment,
} from '../../../../common/helpers/signingFlowV2'
import * as r from '../../../../react-utils'

import * as cp from '../../../comps'
import { productPhotoMap } from '../../../img/productPhotos/productPhotoMap'

import { getSummaryNotes } from '../Summary/Cost'
import ProductViewer from '../Summary/ProductViewer'
import {
  SUMMARY_DETAIL_FIELDS,
  ViewerProduct,
  getRowProductPhotos,
  getViewerProducts,
  orderSummaryFields,
  viewerProductKey,
} from '../Summary/jobSummaryProducts'

//@ts-ignore
import styles from './OtherProviders.module.css'

const photoFor = (type: string): string | undefined => productPhotoMap[type]

/** The lease total the comparison paints red: the longest horizon. */
const LEASE_TOTAL_YEARS = 15

export interface OtherProvidersProps {
  customerID: string
  company: string
  readOnly?: boolean
}

/**
 * The products the viewer steps through, in the thumbnail order of this page
 * (the same helper the Job Summary uses). The five detail fields are always
 * read so the hook order never changes.
 */
const useViewerProducts = (
  customerID: string,
  fields: readonly c.CustomerField[],
): readonly ViewerProduct[] => {
  const { rows: cams } = r.useRowable(customerID, 'security_cams_detail')
  const { rows: homeAutomation } = r.useRowable(
    customerID,
    'security_home_automation_detail',
  )
  const { rows: system } = r.useRowable(customerID, 'security_system_detail')
  const { rows: tv } = r.useRowable(customerID, 'security_tv_detail')
  const { rows: wifiCameras } = r.useRowable(
    customerID,
    'security_wifi_cameras_detail',
  )
  return React.useMemo(() => {
    const rowsByField: Readonly<Partial<Record<string, readonly object[]>>> = {
      security_cams: cams,
      security_home_automation: homeAutomation,
      security_system: system,
      security_tv: tv,
      security_wifi_cameras: wifiCameras,
    }
    return getViewerProducts(
      fields.flatMap((field) => {
        const rows = rowsByField[field]
        return rows ? [{ field, rows }] : []
      }),
      photoFor,
    )
  }, [cams, fields, homeAutomation, system, tv, wifiCameras])
}

/**
 * "Other providers" comparison page (Jaron, #development-dpt Oct 15-16,
 * ticket 13312401562): "a comparison between the other companies and us",
 * right after the product line-up and before "Who really owns your…".
 *
 * Left, the customer's current provider (Brinks unless the rep types another
 * name): what they LEASE, the problems the customer mentioned, and the
 * monitoring going up by the inflation the rep types (3% unless changed) every
 * year, totalled over 5/10/15 years. Right, HomeCam: what the
 * customer OWNS, read from the same rows as the Job Summary, the monitoring,
 * the monthly payment and the total.
 *
 * The rep turns it on per customer and edits it right here (Jaron: "edit the
 * Brinks from here or from the front end"); the customer's signing link only
 * shows it once it is on. Marketing, not contract: kept off the PDF like the
 * line-up and SalesSlick.
 */
export default React.memo<OtherProvidersProps>(function OtherProviders({
  customerID,
  company,
  readOnly,
}) {
  const [customer] = r.useCustomer(customerID)
  const [onRaw, onWrite] = r.useCustomerField(
    customerID,
    'other_provider_compare',
  )
  const [nameVal, nameWrite] = r.useCustomerDraftField(
    customerID,
    'other_provider_name',
  )
  const [equipmentVal, equipmentWrite] = r.useCustomerDraftField(
    customerID,
    'other_provider_equipment',
  )
  const [rowsVal, rowsWrite] = r.useCustomerDraftField(
    customerID,
    'other_provider_equipment_rows',
  )
  const [issuesVal, issuesWrite] = r.useCustomerDraftField(
    customerID,
    'other_provider_issues',
  )
  const [monthlyVal, monthlyWrite] = r.useCustomerDraftField(
    customerID,
    'other_provider_monthly',
  )
  const [inflationVal, inflationWrite] = r.useCustomerDraftField(
    customerID,
    'other_provider_inflation',
  )
  const { appliedLoan, paymentData } = r.usePayment(customerID)
  const [monthlyTyped] = r.useCustomerField(
    customerID,
    'home_improvement_monthly_payment',
  )
  const companyTotalCost = r.useTotalCost('company', customerID, false)

  const on = c.otherProviderComparisonOn(onRaw)
  const editable = !readOnly
  const providerName = c.otherProviderName(nameVal)
  const providerMonthly = c.otherProviderMonthly(monthlyVal)
  const inflationPercent = c.otherProviderInflationPercent(inflationVal)
  const inflation = c.otherProviderInflation(inflationVal)

  // HomeCam side: the security categories the Job Summary prints, priced the
  // same way (the "as applied" preview before Apply, 13312378676).
  const previewGrandTotal =
    !appliedLoan &&
    paymentData != null &&
    typeof paymentData.grandTotalWDF === 'number' &&
    paymentData.grandTotalWDF > 0
      ? paymentData.grandTotalWDF
      : 0
  // Job Summary order (cameras, WiFi, system, ...), not the alphabetical
  // order of the field names: WiFi cameras read above the sensors (Jaron).
  const ownedKey = (Object.keys(SUMMARY_DETAIL_FIELDS) as c.CustomerField[])
    .filter(
      (field) =>
        customer[field] === 'yes' &&
        c.parseFloating(
          customer[(field + '_customer_cost') as c.CustomerField],
        ) > 0,
    )
    .join(',')
  const ownedFields = React.useMemo(
    () =>
      orderSummaryFields(
        (ownedKey ? ownedKey.split(',') : []) as c.CustomerField[],
      ),
    [ownedKey],
  )
  const ownedTotal = ownedFields.reduce((acc, field) => {
    const saved = c.parseFloating(
      customer[(field + '_customer_cost') as c.CustomerField],
    )
    const asApplied =
      previewGrandTotal > 0 && companyTotalCost > 0
        ? (previewGrandTotal *
            c.parseFloating(customer[(field + '_cost') as c.CustomerField])) /
          companyTotalCost
        : 0
    return acc + (asApplied > 0 ? asApplied : saved)
  }, 0)
  // Same monthly the Job Summary shows: the lender's figure the rep typed,
  // else the applied loan, else (rep view only) the estimate.
  const monthlyPayment =
    effectiveMonthlyPayment(monthlyTyped, appliedMonthlyPayment(appliedLoan)) ||
    (editable ? appliedMonthlyPayment(paymentData) : 0)

  // One viewer for the page, so it can swipe from product to product like the
  // Job Summary one. The open product is kept even if a live update drops it
  // from the list: the viewer stays, without arrows.
  const viewerProducts = useViewerProducts(customerID, ownedFields)
  const [openProduct, setOpenProduct] = React.useState<ViewerProduct | null>(
    null,
  )
  const handleOpenProduct = React.useCallback(
    (field: string, type: string) =>
      setOpenProduct({
        key: viewerProductKey(field, type),
        photo: photoFor(type) ?? '',
        type,
      }),
    [],
  )
  const handleCloseProduct = React.useCallback(() => setOpenProduct(null), [])
  const openIndex = openProduct
    ? viewerProducts.findIndex(({ key }) => key === openProduct.key)
    : -1
  const handlePrevProduct = React.useCallback(() => {
    const prev = viewerProducts[openIndex - 1]
    if (openIndex > 0 && prev) setOpenProduct(prev)
  }, [openIndex, viewerProducts])
  const handleNextProduct = React.useCallback(() => {
    const next = viewerProducts[openIndex + 1]
    if (openIndex >= 0 && next) setOpenProduct(next)
  }, [openIndex, viewerProducts])
  const viewerNav = React.useMemo(
    () =>
      openIndex >= 0 && viewerProducts.length > 1
        ? {
            index: openIndex,
            onNext: handleNextProduct,
            onPrev: handlePrevProduct,
            total: viewerProducts.length,
          }
        : undefined,
    [handleNextProduct, handlePrevProduct, openIndex, viewerProducts.length],
  )

  // The lease column's equipment rows (the app's Alarm tab). Null until the
  // field holds a list: then the legacy text list stays as it was.
  const equipmentRows = React.useMemo(
    () => c.parseEquipmentRows(rowsVal),
    [rowsVal],
  )
  const handleRowsChange = React.useCallback(
    (next: readonly c.OtherProviderEquipmentRow[]) =>
      rowsWrite(c.serializeEquipmentRows(next)),
    [rowsWrite],
  )

  // The rows' photos get a viewer of their own (apart from the HomeCam one):
  // it steps only through the lease rows that have a photo, in row order. The
  // open row is kept by id, like the HomeCam viewer.
  const photoRows = React.useMemo<readonly ViewerProduct[]>(
    () =>
      (equipmentRows ?? []).flatMap((row) =>
        row.photo
          ? [
              {
                key: row.id,
                photo: row.photo,
                type: c.otherProviderEquipmentLabel(row.type),
              },
            ]
          : [],
      ),
    [equipmentRows],
  )
  const [openPhoto, setOpenPhoto] = React.useState<ViewerProduct | null>(null)
  const handleOpenPhoto = React.useCallback(
    (row: c.OtherProviderEquipmentRow) =>
      setOpenPhoto({
        key: row.id,
        photo: row.photo,
        type: c.otherProviderEquipmentLabel(row.type),
      }),
    [],
  )
  const handleClosePhoto = React.useCallback(() => setOpenPhoto(null), [])
  const openPhotoIndex = openPhoto
    ? photoRows.findIndex(({ key }) => key === openPhoto.key)
    : -1
  const handlePrevPhoto = React.useCallback(() => {
    const prev = photoRows[openPhotoIndex - 1]
    if (openPhotoIndex > 0 && prev) setOpenPhoto(prev)
  }, [openPhotoIndex, photoRows])
  const handleNextPhoto = React.useCallback(() => {
    const next = photoRows[openPhotoIndex + 1]
    if (openPhotoIndex >= 0 && next) setOpenPhoto(next)
  }, [openPhotoIndex, photoRows])
  const photoNav = React.useMemo(
    () =>
      openPhotoIndex >= 0 && photoRows.length > 1
        ? {
            index: openPhotoIndex,
            onNext: handleNextPhoto,
            onPrev: handlePrevPhoto,
            total: photoRows.length,
          }
        : undefined,
    [handleNextPhoto, handlePrevPhoto, openPhotoIndex, photoRows.length],
  )

  if (!on) {
    if (!editable) return null
    return (
      <cp.PageReport autoHeight company={company} companyBorder hPad noPDF>
        <div className={styles['off']}>
          <button
            className={styles['turn-on']}
            onClick={() => onWrite('yes')}
            type="button"
          >
            + Compare with the customer's current provider
          </button>
        </div>
      </cp.PageReport>
    )
  }

  return (
    <cp.PageReport autoHeight company={company} companyBorder hPad noPDF>
      <div className={styles['sheet']}>
        <h2 className={styles['title']}>Other providers</h2>

        {/* A 2x2 grid: row 1 the two lists, row 2 the two money blocks, so
            the top lines of the money blocks line up whatever the lists hold
            (Jaron: "make these even"). Still two columns on a phone. */}
        <div className={styles['columns']}>
          <section className={styles['col']}>
            {editable ? (
              <input
                aria-label="Current provider name"
                className={styles['name-input']}
                onChange={(e) => nameWrite(e.target.value)}
                placeholder={c.DEFAULT_OTHER_PROVIDER_NAME}
                value={nameVal || ''}
              />
            ) : (
              <h3 className={styles['name']}>{providerName}</h3>
            )}
            <div className={styles['band']}>Lease</div>

            {equipmentRows ? (
              <EquipmentRows
                editable={editable}
                onChange={handleRowsChange}
                onOpenPhoto={handleOpenPhoto}
                rows={equipmentRows}
              />
            ) : editable ? (
              <BulletEditor
                className={styles['list']}
                inputClassName={styles['line-input']}
                label="Equipment"
                onChange={equipmentWrite}
                placeholder="1 x Camera"
                value={equipmentVal}
              />
            ) : (
              <ul className={styles['list']}>
                {c.textLines(equipmentVal).map((line, i) => (
                  <li key={`${i}-${line}`}>{line}</li>
                ))}
              </ul>
            )}

            {/* Pushed to the bottom of the lease column, right above the
                Monitoring line. */}
            <div className={styles['issues-block']}>
              <h4 className={styles['issues-title']}>Issues</h4>
              {editable ? (
                <BulletEditor
                  className={styles['issues']}
                  inputClassName={styles['issue-input']}
                  label="Issue"
                  onChange={issuesWrite}
                  placeholder="Doorbell not working"
                  value={issuesVal}
                />
              ) : (
                <ul className={styles['issues']}>
                  {c.textLines(issuesVal).map((line, i) => (
                    <li key={`${i}-${line}`}>{line}</li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className={`${styles['col']} ${styles['col-right']}`}>
            <h3 className={styles['name']}>HomeCam</h3>
            <div className={styles['band']}>Own</div>

            <div className={styles['owned']}>
              {ownedFields.map((field) => (
                <OwnedRow
                  customerID={customerID}
                  field={field}
                  key={field}
                  onOpenProduct={handleOpenProduct}
                />
              ))}
            </div>
          </section>

          <div className={styles['money-cell']}>
            <div className={styles['money']}>
              <p>
                Monitoring:{' '}
                {editable ? (
                  <span>
                    $
                    <input
                      aria-label="Current provider monthly monitoring"
                      className={styles['money-input']}
                      inputMode="decimal"
                      onChange={(e) => monthlyWrite(e.target.value)}
                      placeholder={String(c.DEFAULT_OTHER_PROVIDER_MONTHLY)}
                      value={monthlyVal || ''}
                    />
                    /mo
                  </span>
                ) : (
                  <strong>{c.money(providerMonthly)}/mo</strong>
                )}
              </p>
              <p>
                Inflation:{' '}
                {editable ? (
                  <span>
                    <input
                      aria-label="Current provider yearly inflation percent"
                      className={styles['money-input']}
                      inputMode="decimal"
                      onChange={(e) => inflationWrite(e.target.value)}
                      placeholder={String(
                        c.DEFAULT_OTHER_PROVIDER_INFLATION_PERCENT,
                      )}
                      value={inflationVal || ''}
                    />
                    %
                  </span>
                ) : (
                  <strong>{inflationPercent}%</strong>
                )}{' '}
                <span className={styles['muted']}>every year</span>
              </p>
              {c.COMPARISON_YEARS.map((years) => (
                <p className={styles['row']} key={years}>
                  <span>{years} years</span>
                  <strong
                    className={
                      years === LEASE_TOTAL_YEARS
                        ? styles['lease-total']
                        : undefined
                    }
                  >
                    {c.money(
                      c.monitoringOverYears(providerMonthly, years, inflation),
                    )}
                  </strong>
                </p>
              ))}
            </div>
          </div>

          <div className={`${styles['money-cell']} ${styles['col-right']}`}>
            <div className={styles['money']}>
              <p>
                Monitoring:{' '}
                <strong className={styles['monitoring-own']}>
                  {c.money(MONITORING_MONTHLY)}/mo
                </strong>
              </p>
              {monthlyPayment > 0 && (
                <p className={styles['row']}>
                  <span>Monthly payment</span>
                  <strong>{c.money(monthlyPayment)}/mo</strong>
                </p>
              )}
              {ownedTotal > 0 && (
                <p className={styles['row-total']}>
                  <span>Total</span>
                  <strong>
                    {totalCostPendingApply(customer, c.IS_MAIN)
                      ? TOTAL_PENDING_LABEL
                      : c.money(ownedTotal)}
                  </strong>
                </p>
              )}
            </div>
          </div>
        </div>

        {openProduct && (
          <ProductViewer
            nav={viewerNav}
            onClose={handleCloseProduct}
            photo={openProduct.photo}
            productKey={openProduct.key}
            type={openProduct.type}
          />
        )}

        {openPhoto && (
          <ProductViewer
            nav={photoNav}
            onClose={handleClosePhoto}
            photo={openPhoto.photo}
            productKey={openPhoto.key}
            type={openPhoto.type}
          />
        )}

        {editable && (
          <button
            className={styles['turn-off']}
            onClick={() => onWrite('')}
            type="button"
          >
            Remove this page
          </button>
        )}
      </div>
    </cp.PageReport>
  )
})

/**
 * A bullet list typed in place (Jaron's sketch: "we can write notes w/ bullet
 * points that add when press Enter"). Enter after a line opens a new bullet
 * below it, Backspace on an empty bullet removes it, and + adds one at the
 * end. Stored as one item per line, the same text `textLines` reads back.
 */
const BulletEditor = ({
  className,
  inputClassName,
  label,
  onChange,
  placeholder,
  value,
}: {
  className?: string
  inputClassName?: string
  label: string
  onChange: (val: string) => void
  placeholder: string
  value: string
}) => {
  const lines = (value || '').split('\n')
  const inputs = React.useRef<(HTMLInputElement | null)[]>([])
  const [focusAt, setFocusAt] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (focusAt === null) return
    inputs.current[focusAt]?.focus()
    setFocusAt(null)
  }, [focusAt])

  const write = (next: string[]) => onChange(next.join('\n'))

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      write([...lines.slice(0, i + 1), '', ...lines.slice(i + 1)])
      setFocusAt(i + 1)
    } else if (e.key === 'Backspace' && lines[i] === '' && lines.length > 1) {
      e.preventDefault()
      write(lines.filter((_, j) => j !== i))
      setFocusAt(Math.max(0, i - 1))
    }
  }

  const add = () => {
    const last = lines[lines.length - 1]
    if (last !== undefined && last.trim() === '') {
      setFocusAt(lines.length - 1)
      return
    }
    write([...lines, ''])
    setFocusAt(lines.length)
  }

  return (
    <>
      <ul className={className}>
        {lines.map((line, i) => (
          <li key={i}>
            <input
              aria-label={label + ' ' + (i + 1)}
              className={inputClassName}
              enterKeyHint="next"
              onChange={(e) => {
                const next = [...lines]
                next[i] = e.target.value
                write(next)
              }}
              onKeyDown={(e) => onKeyDown(i, e)}
              placeholder={i === 0 ? placeholder : ''}
              ref={(el) => {
                inputs.current[i] = el
              }}
              value={line}
            />
          </li>
        ))}
      </ul>
      <button
        aria-label={'Add ' + label.toLowerCase()}
        className={styles['add']}
        onClick={add}
        type="button"
      >
        +
      </button>
    </>
  )
}

/** Quantity bounds of an equipment row, the helper's (1-99). */
const MIN_EQUIPMENT_QTY = 1
const MAX_EQUIPMENT_QTY = 99

/**
 * The lease column's equipment rows. The customer (and the signing link) reads
 * one bullet per row with the customer-safe label and a thumbnail button when
 * the row has a photo. The rep edits the type and the quantity, removes a row
 * or adds one; photos are taken only in the app, so there is no upload here.
 */
const EquipmentRows = ({
  editable,
  onChange,
  onOpenPhoto,
  rows,
}: {
  editable: boolean
  onChange: (next: readonly c.OtherProviderEquipmentRow[]) => void
  onOpenPhoto: (row: c.OtherProviderEquipmentRow) => void
  rows: readonly c.OtherProviderEquipmentRow[]
}) => {
  const thumb = (row: c.OtherProviderEquipmentRow) =>
    row.photo ? (
      <button
        aria-label={`View ${c.otherProviderEquipmentLabel(row.type)}`}
        className={`${styles['photo-button']} ${styles['equip-thumb']}`}
        onClick={() => onOpenPhoto(row)}
        type="button"
      >
        <img alt="" src={row.photo} />
      </button>
    ) : null

  if (!editable) {
    return (
      <ul className={styles['list']}>
        {rows.map((row) => (
          <li className={styles['equip-item']} key={row.id}>
            <span>{c.equipmentRowText(row)}</span>
            {thumb(row)}
          </li>
        ))}
      </ul>
    )
  }

  const update = (id: string, patch: Partial<c.OtherProviderEquipmentRow>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)))
  const knownTypes: readonly string[] = c.OTHER_PROVIDER_EQUIPMENT_TYPES

  return (
    <>
      <ul className={`${styles['list']} ${styles['equip-list']}`}>
        {rows.map((row) => (
          <li className={styles['equip-row']} key={row.id}>
            <select
              aria-label="Equipment type"
              className={styles['equip-type']}
              onChange={(e) => update(row.id, { type: e.target.value })}
              value={row.type}
            >
              {!knownTypes.includes(row.type) && (
                <option value={row.type}>{row.type || 'Select type'}</option>
              )}
              {knownTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <input
              aria-label="Equipment quantity"
              className={`${styles['line-input']} ${styles['equip-qty']}`}
              inputMode="numeric"
              max={MAX_EQUIPMENT_QTY}
              min={MIN_EQUIPMENT_QTY}
              onChange={(e) => {
                const n = Math.floor(Number(e.target.value))
                if (Number.isFinite(n) && n >= MIN_EQUIPMENT_QTY) {
                  update(row.id, { qty: Math.min(n, MAX_EQUIPMENT_QTY) })
                }
              }}
              onFocus={(e) => e.target.select()}
              type="number"
              value={row.qty}
            />
            {thumb(row)}
            <button
              aria-label="Remove equipment"
              className={styles['equip-remove']}
              onClick={() => onChange(rows.filter(({ id }) => id !== row.id))}
              type="button"
            >
              x
            </button>
          </li>
        ))}
      </ul>
      <button
        aria-label="Add equipment"
        className={styles['add']}
        onClick={() =>
          onChange([
            ...rows,
            {
              id: c.newEquipmentRowId(),
              photo: '',
              qty: 1,
              type: c.OTHER_PROVIDER_EQUIPMENT_TYPES[0],
            },
          ])
        }
        type="button"
      >
        +
      </button>
    </>
  )
}

/**
 * One HomeCam category: its product notes (the same short notes the Job
 * Summary prints) and the product photos.
 */
const OwnedRow = React.memo<{
  customerID: string
  field: c.CustomerField
  onOpenProduct: (field: string, type: string) => void
}>(function OwnedRow({ customerID, field, onOpenProduct }) {
  const [customer] = r.useCustomer(customerID)
  const detailField = SUMMARY_DETAIL_FIELDS[field]!
  const { rows } = r.useRowable(customerID, detailField)
  const { categoryPhotos, s4Photo } = React.useMemo(
    () => getRowProductPhotos(rows as readonly object[], photoFor),
    [rows],
  )
  const notes = getSummaryNotes(customer, field)
  const lines = c.shortProductNotes(
    notes.auto,
    notes.custom,
    true,
    c.SHORT_NOTES_MAX_LINES,
    false,
  )
  return (
    <div className={styles['owned-row']}>
      <ul className={styles['owned-notes']}>
        {lines.map(({ kind, text }, i) =>
          kind === 'separator' ? (
            <li className={styles['owned-sep']} key={i} />
          ) : (
            <li
              className={
                kind === 'product' ? styles['owned-product'] : undefined
              }
              key={i}
            >
              {text}
            </li>
          ),
        )}
      </ul>
      <div className={styles['owned-photos']}>
        {/* The S4 thumbnail comes last, as in the viewer's list. */}
        {[...categoryPhotos, ...(s4Photo ? [s4Photo] : [])].map((photo) => (
          <button
            aria-label={`View ${c.resolveAlarmSafeProductName(photo.type)}`}
            className={styles['photo-button']}
            key={photo.type}
            onClick={() => onOpenProduct(field, photo.type)}
            type="button"
          >
            <img alt="" src={photo.url} />
          </button>
        ))}
      </div>
    </div>
  )
})
